import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Backdrop,
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import { useDebounce } from "Hooks/useDebounce";
import { useSearchParams } from "Navigation/useSearchParams";
import { SEARCH_TERM_PARAM } from "Pages/People/constants";
import { UserSearchResults } from "Pages/People/UserSearchResults";
import { useRef, useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Social.People;

type Props = {
  /** Replaces the generic "Search" hint where the page needs to say what is being searched. */
  placeholder?: string;
};

/**
 * In-place people search for the People page: while open, the results float in
 * a panel over the content below instead of pushing it down. Exits: ESC abandons
 * the search (clears + closes), the X in the input clears the term but keeps
 * searching, an outside click closes the panel but keeps the term.
 */
export const UserSearchOverlay = ({ placeholder }: Props) => {
  const { t } = useResource();
  const [searchParams, setSearchParams] = useSearchParams();

  // The input is controlled by local state — a URL-controlled value lags a router
  // navigation behind the keyboard and drops characters on fast typing. The term
  // is mirrored into the URL (replace, not push — one history entry per visit) as
  // a fire-and-forget side effect, read back only on mount, so coming back from a
  // profile detail restores the search.
  const [searchTerm, setSearchTermState] = useState(
    () => searchParams.get(SEARCH_TERM_PARAM) ?? "",
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const setSearchTerm = (term: string) => {
    setSearchTermState(term);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (term) {
          next.set(SEARCH_TERM_PARAM, term);
        } else {
          next.delete(SEARCH_TERM_PARAM);
        }
        return next;
      },
      { replace: true },
    );
  };

  // A term restored from the URL reopens the panel with its results.
  const [isOpen, setIsOpen] = useState(searchTerm !== "");
  const inputRef = useRef<HTMLInputElement>(null);

  const abandonSearch = () => {
    setSearchTerm("");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const clearSearchTerm = () => {
    setSearchTerm("");
    inputRef.current?.focus();
  };

  // Visually one paper: the input paper drops its bottom corners while open and
  // the panel drops its top ones, sits flush below (no margin) and skips its top
  // padding. Each half clips its shadow on the seam side (clip-path with room for
  // the shadow on the other sides), so neither bleeds onto the other.
  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Box
        position="relative"
        // While open, the whole unit rides above the backdrop (panel keeps its
        // own modal zIndex; the input paper inherits this stacking context).
        zIndex={isOpen ? "modal" : undefined}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            abandonSearch();
          }
        }}
      >
        {/* Same dimmed-blur treatment as the user menu / notifications popover.
            Rendered inside the ClickAwayListener subtree, so it needs its own
            close handler — clicks on it never count as "away". */}
        <Backdrop
          open={isOpen}
          onClick={() => setIsOpen(false)}
          sx={{
            zIndex: ({ zIndex }) => zIndex.modal - 1,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.1)",
          }}
        />
        <PrimaryPaper
          sx={{
            mt: 0,
            ...(isOpen && {
              // above the backdrop, like the results panel
              position: "relative",
              zIndex: "modal",
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              clipPath: "inset(-24px -24px 0 -24px)",
            }),
          }}
        >
          <TextInput
            value={searchTerm}
            onInputChange={(e) => {
              setSearchTerm(e.target.value);
              setIsOpen(true);
            }}
            placeholder={placeholder ?? t(RESOURCES.SearchPlaceholder)}
            autoComplete="off"
            disableHelperText
            inputRef={inputRef}
            onFocus={() => setIsOpen(true)}
            inputStartAdornment={
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            }
            inputEndAdornment={
              searchTerm !== "" ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t(Resources.Common.Cancel)}
                    onClick={clearSearchTerm}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }
          />
        </PrimaryPaper>
        {isOpen && (
          <PrimaryPaper
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: (theme) => theme.zIndex.modal,
              mt: 0,
              // && outranks the styled paper's breakpoint padding shorthand
              "&&": { pt: 0 },
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              clipPath: "inset(0 -24px -24px -24px)",
            }}
          >
            <Stack gap={3}>
              <UserSearchResults
                searchTerm={searchTerm}
                debouncedSearchTerm={debouncedSearchTerm}
              />
            </Stack>
          </PrimaryPaper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
